import React, { useCallback, useState, useEffect, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Selectable from './CreativeBlockSelectable';
import ContextMenu from '../../../artboard/ContextMenu';
import { FOOTER, HEADER, dummyCallback } from '../../../utils/complexDocumentConstants';
import { useDocumentContext, COMPLEX_DOCUMENT_CONTEXT_KEYS } from '../../../context/complexDocumentContext';
import { PageElementsContext } from '../../../context/pageElementsContext';
import { CONTEXT_KEYS, useArtboardContext } from '../../../context/complexArtboardContext';
import { generateAndInsertText } from '../../../utils/TextBlockHelper';
import { message } from 'antd';
import { getClipboardData, resetClipboardData } from '../../../utils/helper';
import { COMPLEX_PAGE_OPTIONS } from '../translationValues';
import { GroupNameModal } from '../../../artboard/ContextMenu/GroupNameModal';

const NAMESPACES = ['COMPLEX_PAGE_OPTIONS'];
const STATIC_TYPES = [HEADER, FOOTER];

const CreativeDesignContextWrapper = ({
  itemUUID,
  pageId,
  scaling,
  editable,
  dropElementFnRef,
  selectedElementIds,
  setSelectedElementIds,
  clearSelection,
  parentUUID,
  moveableReference,
  staticTableBlock = false,
  setSelectedGroupElementId,
  selectedGroupElementId,
  showGroupName
}) => {
  const {
    handleStoredElements,
    deleteElementDetails,
    swapElements,
    pasteElements,
    copyElements,
    storedElements,
    groupingElements,
    setGroupedElementIds,
    setGroupName,
    groupedElementIds
  } = useContext(PageElementsContext);

  const getTargetsRef = useRef(null);
  const isPasteInProgress = useRef(false);

  const { t } = useTranslation(NAMESPACES);

  const [copiedElementIds, setCopiedElements] = useState([]);
  const [clearTargets, setClearTargets] = useState(false);

  const customSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.IS_EMAIL_TEMPLATE], []);
  const [isEmailTemplate] = useDocumentContext(customSelector, `DesignBlock-${itemUUID}`);

  const [isHeaderElement] = useDocumentContext(store => (STATIC_TYPES.includes(store[itemUUID].type)
    || STATIC_TYPES.includes(store[COMPLEX_DOCUMENT_CONTEXT_KEYS.REUSABLE_BLOCK_TYPE])), `DesignBlock-${itemUUID}`);

  const artboardSelector = useCallback(store => null, []);
  const [, updateArtboard] = useArtboardContext(artboardSelector);

  const interactiveDesignSelector = useCallback(store => store[CONTEXT_KEYS.IS_INTERACTIVE_DESIGN] || store[CONTEXT_KEYS.IS_OPEN_DESIGN], []);
  const [isInteractiveDesign] = useArtboardContext(interactiveDesignSelector, `CreativeDesignContextWrapper`);

  const addNewElement = useCallback((elements, isDropped = false) => {
    setClearTargets(true);
    handleStoredElements(elements, isDropped, true);
    setTimeout(() => setClearTargets(false), 100);
  }, []);

  useEffect(() => {
    dropElementFnRef.current = addNewElement;
  }, []);

  const handleSelectedElements = useCallback(targets => {
    let uuids = [];
    setClearTargets(false);
    if (targets && targets.length > 0) {
      uuids = targets.map(targetElement => targetElement.getAttribute('data-uuid'));
      setSelectedElementIds(uuids);
      setGroupedElementIds(uuids);
    } else {
      setSelectedElementIds([]);
    }
  }, []);

  useEffect(() => {
    if(editable && staticTableBlock) {
      setSelectedElementIds([itemUUID]);
    }
  }, [editable]);

  const onKeyPress = useCallback(
    async e => {
      if (
        (e && e.keyCode && (e.keyCode === 46 || e.keyCode === 8)) ||
        (e && e.code && e.code === 'Delete')
      ) {
        if (selectedGroupElementId) {
          e.preventDefault();
          deleteElementDetails([selectedGroupElementId], getTargetsRef?.current, true);
          setClearTargets(true);
          setSelectedGroupElementId();
        } else if (selectedElementIds && selectedElementIds.length > 0) {
          e.preventDefault();
          deleteElementDetails(selectedElementIds, getTargetsRef?.current, true);
          setClearTargets(true);
          setSelectedElementIds([]);
        }
      }
      if ((e.ctrlKey || e.metaKey) && e.which === 67) {
        const copiedElements = copyElements(selectedElementIds, true);
        try {
          const copyElement = {pdsCopiedElements:copiedElements, moveableRect: moveableReference.current?.getRect(), source: itemUUID};
          await navigator.clipboard.writeText(JSON.stringify(copyElement));
        } catch (err) {
          console.error('Failed to write clipboard contents: ', err);
        }
      }
      
      const copiedElements= await getClipboardData();
    
      if ((e.ctrlKey || e.metaKey) && e.which === 86 && copiedElements?.pdsCopiedElements && copiedElements?.pdsCopiedElements.length  && !isPasteInProgress.current) {
        // if an paste operation is already in progress, restrict the multiple paste operations before current copied
        // elements are removed from state. else, crash occurs due to same id for pasting the same elements(ie, text elements) multiple times.
        isPasteInProgress.current = true;
        pasteElements(copiedElements.pdsCopiedElements, () => {
          setTimeout(() => {
            setSelectedElementIds(copiedElements.pdsCopiedElements.map(item => item.uuid));
            setClearTargets(false);
            isPasteInProgress.current = false;
          }, 100);
        }, copiedElements.moveableRect, itemUUID, copiedElements.source);
        setClearTargets(true);
        setSelectedElementIds([]);
        setCopiedElements([]);
        await resetClipboardData();
      }
    },
    [selectedElementIds, copiedElementIds, storedElements, selectedGroupElementId],
  );

  useEffect(() => {
    setClearTargets(clearSelection);
  }, [clearSelection]);

  const [editorRef] = useArtboardContext(
    store => store[CONTEXT_KEYS.TEXT_ELEMENT_REF],
    `DesignBlock-${itemUUID}`,
  );

  const [staticTable] = useArtboardContext(
    store => store[CONTEXT_KEYS.STATIC_TABLE],
    `DesignBlock-${itemUUID}`,
  );

  const toggleAILoader = flag => {
    updateArtboard({
      [CONTEXT_KEYS.AI_LOADER]: flag,
    });
  };

  const generateAndInsertTextInEditor = () => {
    if (editorRef.current) {
      let editor = editorRef?.current.getEditor();
      editor.focus();
      toggleAILoader(true);
      editor &&
        generateAndInsertText(t, editor, (isError = false, failureMessage = undefined) => {
          toggleAILoader(false);
          if(isError) {
            message.error(t(COMPLEX_PAGE_OPTIONS.TEXT_GENERATION_FAILED.key, COMPLEX_PAGE_OPTIONS.TEXT_GENERATION_FAILED.value));
          }
          if (failureMessage !== undefined) {
            updateArtboard({
              [CONTEXT_KEYS.UPGRADE_MODAL_INFO]: {
                visible: true,
                failureMessage,
              },
            });
          }
        });
    }
  };

  return (
    <>
      <Selectable
        editable={editable}
        handleSelectedElements={handleSelectedElements}
        setSelectedGroupElementId={setSelectedGroupElementId}
        selectedGroupElementId={selectedGroupElementId}
        clearTargets={clearTargets}
        selectedElementIds={selectedElementIds}
        // forceRefresh={this.state.forceRefresh}
        pageId={itemUUID}
        parentUUID={parentUUID}
        scrollPageId={pageId}
        onFontClick={dummyCallback}
        parentContained={false}
        onSvgChange={dummyCallback}
        onKeyPress={onKeyPress}
        getTargets={getTargetsRef}
        isEmailTemplate={isEmailTemplate}
        moveableReference={moveableReference}
        staticTableBlock={staticTableBlock}
        isInteractiveDesign={isInteractiveDesign}
        isHeaderElement={isHeaderElement}
        setSelectedElementIds={setSelectedElementIds}
      />
      {editable && (
        <ContextMenu
          documentId={itemUUID}
          scaling={scaling}
          swapElements={swapElements}
          handleModal={dummyCallback}
          multiSelectedElementIds={selectedElementIds}
          showSave={false}
          showGenerateText={editorRef?.current?.getEditor().getSelection()?.length > 0}
          generateTextHandler={generateAndInsertTextInEditor}
          staticTable={staticTable}
          isHeaderElement={isHeaderElement}
          groupingElements={groupingElements}
          flowId={parentUUID}
        />
      )}
      {showGroupName ? (
        <GroupNameModal
          showPopOver={showGroupName}
          groupingElements={groupingElements}
          setGroupName={setGroupName}
          groupedElementIds={groupedElementIds}
        />
      ) : null}
    </>
  );
};

export default CreativeDesignContextWrapper;
